import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import Modal from '../../Modal';
import ApplyPaymentModal from './ApplyPaymentModal';
import EditInvoiceModal from './EditInvoiceModal';

const STATUS_LABELS = {
    draft: 'Draft',
    sent: 'Sent',
    viewed: 'Viewed',
    partially_paid: 'Partially Paid',
    pending_settlement: 'Pending Settlement',
    paid: 'Paid',
    overdue: 'Overdue',
    void: 'Void',
    canceled: 'Canceled',
    refunded: 'Refunded',
    partially_refunded: 'Partially Refunded',
    uncollectible: 'Uncollectible',
};

function fmtCents(cents, currency = 'USD') {
    return `$${(cents / 100).toFixed(2)} ${currency}`;
}

function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function InvoiceDetail({ invoiceId, onClose }) {
    const { fetch: authFetch, channel, API_URL, DASHBOARD_URL } = useAuth();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPayment, setShowPayment] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [messageBody, setMessageBody] = useState('');
    const [noteBody, setNoteBody] = useState('');
    const [activeTab, setActiveTab] = useState('details');

    const loadInvoice = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch(`${API_URL}/v1/seller/invoices/${invoiceId}`);
            if (!response.ok) throw new Error('Failed to load invoice');
            const json = await response.json();
            setInvoice(json.data);
        } catch (err) {
            setError('Failed to load invoice.');
        } finally {
            setLoading(false);
        }
    }, [invoiceId, authFetch, API_URL]);

    useEffect(() => {
        loadInvoice();
    }, [loadInvoice]);

    const performAction = async (path, payload = {}, method = 'POST') => {
        setActionError(null);
        setActionLoading(true);
        try {
            const response = await authFetch(
                `${API_URL}/v1/seller/invoices/${invoice.uuid}${path}`,
                { method, body: JSON.stringify(payload) }
            );
            const json = await response.json();
            if (!response.ok) {
                setActionError(json.error || 'Action failed');
                return null;
            }
            return json.data;
        } catch (err) {
            setActionError('An error occurred.');
            return null;
        } finally {
            setActionLoading(false);
        }
    };

    const handleSend = async () => {
        if (!window.confirm('Send this invoice to the customer?')) return;
        const updated = await performAction('/send');
        if (updated) setInvoice(updated);
    };

    const handleVoid = async () => {
        const reason = window.prompt('Reason for voiding (optional):');
        if (reason === null) return; // cancelled
        const updated = await performAction('/void', { reason });
        if (updated) setInvoice(updated);
    };

    const handleCancel = async () => {
        if (!window.confirm('Cancel this invoice?')) return;
        const updated = await performAction('/cancel');
        if (updated) setInvoice(updated);
    };

    const handleMarkPaid = async () => {
        if (!window.confirm('Mark this invoice as paid? Manually marked invoices are excluded from payout requests.')) return;
        const updated = await performAction('/pay', {
            markAsPaid: true,
            method: 'manual',
            note: 'Marked as paid manually',
            metadata: {
                markedAsPaid: true,
                excludedFromPayoutRequests: true,
            },
        });
        if (updated) setInvoice(updated);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!messageBody.trim()) return;
        const updated = await performAction('/messages', { body: messageBody });
        if (updated) {
            setInvoice(updated);
            setMessageBody('');
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!noteBody.trim()) return;
        const updated = await performAction('/notes', { body: noteBody });
        if (updated) {
            setInvoice(updated);
            setNoteBody('');
        }
    };

    const customerInvoiceUrl = invoice
        ? `${channel?.url || ''}/invoice/${invoice.publicToken}`
        : null;

    if (loading) {
        return (
            <Modal onClose={onClose} title="Invoice" isOpen={true}>
                <p>Loading…</p>
            </Modal>
        );
    }

    if (error || !invoice) {
        return (
            <Modal onClose={onClose} title="Invoice" isOpen={true}>
                <p className="error">{error || 'Invoice not found'}</p>
            </Modal>
        );
    }

    const canSend = invoice.status === 'draft';
    const canVoid = ['draft', 'sent', 'viewed'].includes(invoice.status);
    const canCancel = ['sent', 'viewed', 'overdue'].includes(invoice.status);
    // const canPay = !['void', 'canceled', 'uncollectible'].includes(invoice.status);
    const canPay = false; // disable partial payments for safety
    const canMarkPaid = ['sent', 'viewed', 'overdue', 'partially_paid'].includes(invoice.status)
        && invoice.amountDueCents > 0;
    const canEdit = !['void', 'canceled', 'refunded', 'uncollectible'].includes(invoice.status);
    const isReadOnly = ['void', 'canceled', 'refunded', 'uncollectible'].includes(invoice.status);

    return (
        <Modal
            onClose={onClose}
            title={`Invoice ${invoice.invoiceNumber ? '#' + invoice.invoiceNumber : invoice.uuid.slice(0, 8) + '…'}`}
            isOpen={true}
            size="large"
            className="invoice-modal"
        >
            {actionError && <p className="error">{actionError}</p>}

            {/* Header actions */}
            <div className="invoice-detail-actions">
                <span className={`status-badge pill status-${invoice.status}`}>
                    {STATUS_LABELS[invoice.status] || invoice.status}
                </span>

                {canEdit && (
                    <button className="button small" onClick={() => setShowEdit(true)} disabled={actionLoading}>
                        Edit
                    </button>
                )}
                {canSend && (
                    <button className="button small primary" onClick={handleSend} disabled={actionLoading}>
                        Send to Customer
                    </button>
                )}
                {canPay && (
                    <button className="button small" onClick={() => setShowPayment(true)} disabled={actionLoading}>
                        Record Payment
                    </button>
                )}
                {canMarkPaid && (
                    <button className="button small" onClick={handleMarkPaid} disabled={actionLoading}>
                        Mark as Paid
                    </button>
                )}
                {canVoid && (
                    <button className="button small danger" onClick={handleVoid} disabled={actionLoading}>
                        Void
                    </button>
                )}
                {canCancel && (
                    <button className="button small danger" onClick={handleCancel} disabled={actionLoading}>
                        Cancel
                    </button>
                )}
            </div>

            {/* Customer invoice link */}
            {invoice.status !== 'draft' && customerInvoiceUrl && (
                <div className="invoice-customer-link">
                    <label>Customer invoice link:</label>
                    <input type="text" readOnly value={customerInvoiceUrl} onClick={e => e.target.select()} />
                </div>
            )}

            {/* Tabs */}
            <div className="invoice-tabs">
                {['details', 'payments', 'messages', 'notes', 'history'].map(tab => (
                    <button
                        key={tab}
                        className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        {tab === 'messages' && invoice.messages?.length > 0 && ` (${invoice.messages.length})`}
                        {tab === 'payments' && invoice.payments?.length > 0 && ` (${invoice.payments.length})`}
                    </button>
                ))}
            </div>

            {/* Tab: Details */}
            {activeTab === 'details' && (
                <div className="invoice-tab-content">
                    <div className="invoice-meta">
                        {invoice.customerSnapshot && (
                            <div>
                                <strong>Customer: </strong>
                                <span>{invoice.customerSnapshot.name || '—'}</span>
                                {invoice.customerSnapshot.email && (
                                    <span> &lt;{invoice.customerSnapshot.email}&gt;</span>
                                )}
                            </div>
                        )}
                        <div><strong>Due date:</strong> {fmtDate(invoice.dueAt)}</div>
                        <div><strong>Sent:</strong> {fmtDate(invoice.sentAt)}</div>
                        {invoice.paidAt && <div><strong>Paid:</strong> {fmtDate(invoice.paidAt)}</div>}
                    </div>

                    {/* Line items */}
                    {invoice.lineItems?.length > 0 && (
                        <table className="invoice-line-items-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Unit Price</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.lineItems.map((item, i) => (
                                    <tr key={item.id || i}>
                                        <td data-label="Description">{item.description}</td>
                                        <td data-label="Quantity">{item.quantity}</td>
                                        <td data-label="Per Unit">{fmtCents(item.unitPriceCents, invoice.currency)}</td>
                                        <td data-label="Total">{fmtCents(item.totalCents, invoice.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div className="invoice-totals">
                        <div><span>Subtotal</span><span>{fmtCents(invoice.subtotalCents, invoice.currency)}</span></div>
                        {invoice.shippingCents > 0 && <div><span>Shipping</span><span>{fmtCents(invoice.shippingCents, invoice.currency)}</span></div>}
                        {invoice.taxCents > 0 && <div><span>Tax</span><span>{fmtCents(invoice.taxCents, invoice.currency)}</span></div>}
                        {invoice.discountCents > 0 && <div><span>Discount</span><span>−{fmtCents(invoice.discountCents, invoice.currency)}</span></div>}
                        <div className="total-row"><span><strong>Total</strong></span><span><strong>{fmtCents(invoice.totalCents, invoice.currency)}</strong></span></div>
                        <div><span>Amount Paid</span><span>{fmtCents(invoice.amountPaidCents, invoice.currency)}</span></div>
                        <div><span><strong>Amount Due</strong></span><span><strong>{fmtCents(invoice.amountDueCents, invoice.currency)}</strong></span></div>
                    </div>
                </div>
            )}

            {/* Tab: Payments */}
            {activeTab === 'payments' && (
                <div className="invoice-tab-content">
                    {invoice.payments?.length === 0 ? (
                        <p>No payments recorded yet.</p>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>Note</th>
                                    <th>Processor ID</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.payments.map((p, i) => (
                                    <tr key={p.id || i} className={p.isRefund ? 'refund-row' : ''}>
                                        <td data-label="Date">{fmtDateTime(p.appliedAt)}</td>
                                        <td data-label="Amount">{p.isRefund ? '−' : ''}{fmtCents(p.amountCents, invoice.currency)}</td>
                                        <td data-label="Method">{p.method}</td>
                                        <td data-label="Note">{p.note || '—'}</td>
                                        <td data-label="Processor ID">{p.processorPaymentId || '—'}</td>
                                        <td data-label="Type">{p.isRefund ? 'Refund' : 'Payment'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Tab: Messages */}
            {activeTab === 'messages' && (
                <div className="invoice-tab-content">
                    <div className="invoice-messages">
                        {invoice.messages?.length === 0 ? (
                            <p style={{marginBottom: "1rem"}}>No messages yet.</p>
                        ) : (
                            invoice.messages.map((m, i) => (
                                <div key={m.id || i} className={`message message-${m.from}`}>
                                    <div className="message-header">
                                        <strong>{m.fromName || m.from}</strong>
                                        <span className="message-date">{fmtDateTime(m.createdAt)}</span>
                                    </div>
                                    <p>{m.body}</p>
                                </div>
                            ))
                        )}
                    </div>
                    {!isReadOnly && (
                        <form onSubmit={handleSendMessage} className="message-compose">
                            <textarea
                                placeholder="Send a message to the customer…"
                                value={messageBody}
                                onChange={e => setMessageBody(e.target.value)}
                                rows={3}
                                maxLength={5000}
                            />
                            <button type="submit" className="button small primary" disabled={actionLoading || !messageBody.trim()}>
                                Send
                            </button>
                        </form>
                    )}
                </div>
            )}

            {/* Tab: Notes (seller-only) */}
            {activeTab === 'notes' && (
                <div className="invoice-tab-content">
                    <p className="info"><em>Internal notes are never visible to the customer.</em></p>
                    {invoice.internalNotes?.length === 0 ? (
                        <p>No internal notes.</p>
                    ) : (
                        invoice.internalNotes.map((n, i) => (
                            <div key={n.id || i} className="internal-note">
                                <p className="note-date">{fmtDateTime(n.createdAt)}</p>
                                <p>{n.body}</p>
                            </div>
                        ))
                    )}
                    {!isReadOnly && (
                        <form onSubmit={handleAddNote} className="note-compose">
                            <textarea
                                placeholder="Add an internal note…"
                                value={noteBody}
                                onChange={e => setNoteBody(e.target.value)}
                                rows={3}
                                maxLength={2000}
                            />
                            <button type="submit" className="button small" disabled={actionLoading || !noteBody.trim()}>
                                Add Note
                            </button>
                        </form>
                    )}
                </div>
            )}

            {/* Tab: History (audit events) */}
            {activeTab === 'history' && (
                <div className="invoice-tab-content">
                    {invoice.events?.length === 0 ? (
                        <p>No events recorded.</p>
                    ) : (
                        <table className="events-table">
                            <thead>
                                <tr>
                                    <th>Time</th>
                                    <th>Event</th>
                                    <th>Description</th>
                                    <th>Actor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...invoice.events].reverse().map((ev, i) => (
                                    <tr key={ev.id || i}>
                                        <td data-label="Time">{fmtDateTime(ev.timestamp)}</td>
                                        <td data-label="Event">{ev.type}</td>
                                        <td data-label="Description">{ev.description}</td>
                                        <td data-label="Actor">{ev.actorType || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {showPayment && (
                <ApplyPaymentModal
                    invoice={invoice}
                    onClose={() => setShowPayment(false)}
                    onSuccess={(updated) => {
                        setInvoice(updated);
                        setShowPayment(false);
                    }}
                />
            )}

            {showEdit && (
                <EditInvoiceModal
                    invoice={invoice}
                    onClose={() => setShowEdit(false)}
                    onSuccess={(updated) => {
                        setInvoice(updated);
                        setShowEdit(false);
                    }}
                />
            )}
        </Modal>
    );
}

export default InvoiceDetail;
